/**
 * RequireAuth — auth guard wrapping all (app) and (portal) routes.
 *
 * Behaviour:
 *   - While the /me query is loading (token present but user not yet fetched):
 *     render a full-screen spinner so the layout does not flash.
 *   - On success: hydration already happened in useCurrentUserQuery's onSuccess;
 *     render the child routes via <Outlet />.
 *   - On error or when no token present: redirect to /login, preserving the
 *     `from` location so login can send the user back.
 *   - Forced password change: if user.must_change_password is true AND the
 *     current path is not /settings/password, redirect there with state
 *     { forced: true }. The change-password page always navigates to /login on
 *     success, so a fresh /me is fetched at next login.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useBootAuth, useCurrentUserQuery } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth-store';
import { getAccessToken } from '@/lib/auth/tokens';

/**
 * Forced-change destinations, per user type.  A portal (external) user must NOT
 * be sent to the internal `/settings/password` route — it lives inside AppLayout
 * (RequireUserType allow="internal"), which would bounce the external user back
 * to `/portal`, where this guard re-forces `/settings/password` → an infinite
 * redirect loop (blank/frozen page).  Each audience changes their password on a
 * page that lives inside their OWN layout.
 */
const CHANGE_PASSWORD_PATH_INTERNAL = '/settings/password';
const CHANGE_PASSWORD_PATH_EXTERNAL = '/portal/password';

export default function RequireAuth() {
  const location = useLocation();
  // Run the silent refresh on first mount.  Until bootChecked flips true we
  // do NOT decide based on getAccessToken() — the cookie-backed refresh may
  // still be in flight on a hard reload.
  const { bootChecked } = useBootAuth();
  const { isLoading, isSuccess, isError } = useCurrentUserQuery();
  const user = useAuthStore((s) => s.user);

  // Boot refresh still pending — hold the layout to avoid a /login flash.
  if (!bootChecked) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Boot finished and no token came back — refresh cookie was missing/expired.
  if (getAccessToken() === null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Token present but /me hasn't resolved yet — show a minimal spinner
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // /me returned an error (expired token, etc.) — send to login
  if (isError) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated and user is hydrated
  if (isSuccess) {
    // Forced password change gate: if the backend requires a change and the
    // user is NOT already on their change-password page, redirect them there.
    // The destination is type-aware so external (portal) users land on a route
    // inside the portal layout (see CHANGE_PASSWORD_PATH_* above).
    const changePasswordPath =
      user?.type === 'internal'
        ? CHANGE_PASSWORD_PATH_INTERNAL
        : CHANGE_PASSWORD_PATH_EXTERNAL;
    if (
      user?.must_change_password === true &&
      location.pathname !== changePasswordPath
    ) {
      return (
        <Navigate
          to={changePasswordPath}
          state={{ forced: true, from: location }}
          replace
        />
      );
    }

    return <Outlet />;
  }

  // Fallback (should not reach here)
  return <Navigate to="/login" state={{ from: location }} replace />;
}
